import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Search, 
  Edit, 
  Trash2, 
  MapPin
} from 'lucide-react';
import Button from '../ui/Button';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../hooks/useToast';

interface Address {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  notes?: string;
  usage_count: number;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
}

interface AddressBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAddress: (address: Address) => void;
}

const AddressBookModal: React.FC<AddressBookModalProps> = ({
  isOpen,
  onClose,
  onSelectAddress
}) => {
  const { toast, ToastComponent } = useToast();
  
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state for adding/editing addresses
  const [isEditing, setIsEditing] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    lat: 0,
    lng: 0,
    notes: ''
  });
  
  // Google Maps validation state
  const [addressError, setAddressError] = useState<string | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);



  // Load addresses from database and initialize Google Maps Autocomplete
  useEffect(() => {
    if (isOpen) {
      loadAddresses();
      
      // Initialize Google Maps Autocomplete if available
      if (window.google && window.google.maps && window.google.maps.places && inputRef.current) {
        try {
          const options = {
            componentRestrictions: { country: 'ar' }, // Restrict to Argentina
            fields: ['address_components', 'geometry', 'name', 'formatted_address'],
          };
          
          autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options);
          
          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current?.getPlace();
            
            if (place && place.geometry && place.geometry.location) {
              setAddressError(null);
              setFormData(prev => ({
                ...prev,
                address: place.formatted_address || '',
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              }));
            }
          });
        } catch (error) {
          console.error('Error initializing Google Maps Autocomplete:', error);
          setAddressError('Error al inicializar Google Maps');
        }
      } else {
        setAddressError('Google Maps no está disponible');
      }
    }
    
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isOpen]);

  // Filter addresses based on search query
  useEffect(() => {
    let filtered = addresses;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(addr => 
        addr.name.toLowerCase().includes(query) ||
        addr.address.toLowerCase().includes(query)
      );
    }

    // Sort by usage count, then by name
    filtered.sort((a, b) => {
      if (a.usage_count !== b.usage_count) return b.usage_count - a.usage_count;
      return a.name.localeCompare(b.name);
    });

    setAddresses(filtered);
  }, [searchQuery]);

  // Clean up form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setSearchQuery('');
      setAddressError(null);
      setIsDeleteModalOpen(false);
      setAddressToDelete(null);
    }
  }, [isOpen]);

  const loadAddresses = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('logistics_addresses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAddresses(data || []);
    } catch (error: any) {
      console.error('Error loading addresses:', error);
      toast.error('Error', error.message || 'Error al cargar las direcciones');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      lat: 0,
      lng: 0,
      notes: ''
    });
    setIsEditing(false);
    setEditingAddress(null);
    setAddressError(null);
    setSearchQuery('');
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Error', 'El nombre de la dirección es obligatorio');
      return;
    }

    if (!formData.address.trim()) {
      toast.error('Error', 'La dirección es obligatoria');
      return;
    }

    try {
      setIsLoading(true);
      
      // Check if coordinates are available
      if (formData.lat === 0 && formData.lng === 0) {
        toast.error('Error', 'Debe seleccionar una dirección válida de Google Maps');
        setIsLoading(false);
        return;
      }

      // Use the provided name
      const addressName = formData.name.trim();

      const addressData = {
        name: addressName,
        address: formData.address,
        lat: formData.lat,
        lng: formData.lng,
        notes: formData.notes || ''
      };

      if (isEditing && editingAddress) {
        // Update existing address
        const { error } = await supabase
          .from('logistics_addresses')
          .update(addressData)
          .eq('id', editingAddress.id);

        if (error) throw error;
        toast.success('Dirección actualizada', 'La dirección ha sido actualizada correctamente');
      } else {
        // Create new address
        const { error } = await supabase
          .from('logistics_addresses')
          .insert([addressData]);

        if (error) throw error;
        toast.success('Dirección guardada', 'La dirección ha sido guardada correctamente');
      }

      await loadAddresses();
      resetForm();
    } catch (error: any) {
      console.error('Error saving address:', error);
      toast.error('Error', error.message || 'Error al guardar la dirección');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setFormData({
      name: address.name,
      address: address.address,
      lat: address.lat,
      lng: address.lng,
      notes: address.notes || ''
    });
    setIsEditing(true);
    setSearchQuery('');
  };

  const openDeleteModal = (address: Address) => {
    setAddressToDelete(address);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAddressToDelete(null);
  };

  const handleDelete = async () => {
    if (!addressToDelete) return;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('logistics_addresses')
        .delete()
        .eq('id', addressToDelete.id);

      if (error) throw error;
      
      toast.success('Dirección eliminada', 'La dirección ha sido eliminada correctamente');
      await loadAddresses();
      closeDeleteModal();
    } catch (error: any) {
      console.error('Error deleting address:', error);
      toast.error('Error', error.message || 'Error al eliminar la dirección');
    } finally {
      setIsLoading(false);
    }
  };





  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <MapPin className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Agenda de Direcciones
            </h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
          >
            Cerrar
          </Button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Left Panel - Address List */}
          <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 p-6 overflow-y-auto">
            {/* Search and Filters */}
            <div className="space-y-4 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar direcciones..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>

            {/* Address List */}
            <div className="space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                </div>
              ) : addresses.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {searchQuery 
                    ? 'No hay direcciones que coincidan con la búsqueda' 
                    : 'No hay direcciones guardadas'}
                </div>
              ) : (
                addresses.map(address => (
                  <div
                    key={address.id}
                    className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 cursor-pointer transition-all hover:shadow-md"
                    onClick={() => onSelectAddress(address)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <MapPin className="h-4 w-4 text-blue-600" />
                          <h3 className="font-medium text-gray-900 dark:text-white truncate">
                            {address.name}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {address.address}
                        </p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                          <span>📍 {address.usage_count} usos</span>
                          {address.notes && (
                            <span className="truncate">📝 {address.notes}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-1 ml-2">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Edit className="h-3 w-3" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(address);
                          }}
                          className="p-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Trash2 className="h-3 w-3" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(address);
                          }}
                          className="p-1 text-red-500 hover:text-red-700"
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Add/Edit Form */}
          <div className="w-1/2 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {isEditing ? 'Editar Dirección' : 'Agregar Dirección'}
              </h3>
              {isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetForm}
                >
                  Cancelar
                </Button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nombre de la Dirección <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Casa, Oficina, Cliente Juan, etc."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Dirección Completa <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={formData.address}
                    onChange={(e) => {
                      setFormData({ ...formData, address: e.target.value });
                      setAddressError(null);
                    }}
                    placeholder="Escriba la dirección para validar con Google Maps"
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      addressError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                </div>
              </div>
              
              {addressError && (
                <div className="text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                  {addressError}
                </div>
              )}
              

              
              {formData.lat !== 0 && formData.lng !== 0 && (
                <div className="text-green-600 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-md flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  Dirección validada: {formData.address}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Guardar')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && addressToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Eliminar Dirección
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Esta acción no se puede deshacer
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300">
                ¿Está seguro de que desea eliminar la dirección <strong>"{addressToDelete.name}"</strong>?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {addressToDelete.address}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={closeDeleteModal}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={isLoading}
                icon={isLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Trash2 className="h-4 w-4" />}
              >
                {isLoading ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {ToastComponent}
    </div>
  );
};

export default AddressBookModal;
